import {
  infiniteQueryOptions,
  keepPreviousData,
  queryOptions,
  useQuery
} from '@tanstack/react-query';

import type { GetReviewsAllQuery } from '@schema/cart';

import API from '@api';
import { getURLProps } from '@utils/setPagesUrl';
import { withSsrCache } from '../../ssrCache';

export const REVIEWS_LIMIT = 20;

/** Дефолты фильтров страницы /reviews (совпадают с defaultData getURLProps) */
export const REVIEWS_SORT_DEFAULT = 'Fresh';
export const REVIEWS_SOURCE_DEFAULT = 'All';

export type ReviewsListParamsT = {
  setLimit: number;
  setSource: string;
  setSort: string;
};

/**
 * ЭТАП 2 — параметры списка отзывов из /r/-хвоста (/r/sort/Positive,
 * /r/source/YM, /r/page/N). ЕДИНЫЙ источник для компонента
 * (ReviewPageReviews) и SSR-префетча: ключ обязан совпасть байт-в-байт.
 */
export const buildReviewsListParams = (
  pathname: string
): { params: ReviewsListParamsT; page: number } => {
  const {
    sort = REVIEWS_SORT_DEFAULT,
    source = REVIEWS_SOURCE_DEFAULT,
    page
  } = getURLProps({ pathname });

  return {
    params: {
      setLimit: REVIEWS_LIMIT,
      setSource: source,
      setSort: sort
    },
    page: Number(page || 1)
  };
};

type GetReviewsAllDataT = NonNullable<GetReviewsAllQuery['getReviewsAll']>;

export type ReviewItemT = NonNullable<
  GetReviewsAllDataT['reviewsItems']
>[number];

export const reviewsInfiniteQueryOptions = (
  params: Omit<Parameters<typeof API.getReviews>[0], 'page'>,
  initialPage = 1
) => {
  const cacheKey = ['reviews', 'infinite', params];

  return withSsrCache(
    infiniteQueryOptions({
      queryKey: cacheKey,
      queryFn: ({ pageParam }) => {
        const perPage = params.setLimit ?? REVIEWS_LIMIT;

        return API.getReviews({ ...params, page: pageParam }).then(res => {
          const data = res.getReviewsAll;
          const totalCount = data.totalCount || 0;
          const totalCountPages = Math.ceil(totalCount / perPage);

          return {
            items: data.reviewsItems || [],
            total: totalCount,
            pagination: {
              total: totalCountPages,
              current: pageParam
            }
          };
        });
      },
      initialPageParam: initialPage,
      getNextPageParam: (lastPage, _allPages, lastPageParam) => {
        const totalCount = lastPage.total;
        const limit = params.setLimit ?? REVIEWS_LIMIT;

        return lastPageParam * limit < totalCount
          ? lastPageParam + 1
          : undefined;
      },
      placeholderData: keepPreviousData
    })
  );
};

export type ReviewsInfiniteQueryOptionsT = ReturnType<
  typeof reviewsInfiniteQueryOptions
>;

/**
 * Блок «Отзывы о товарах» на главной: свежие отзывы о товарах.
 * Кэш как у остальных блоков главной (карусели товаров, рецепты).
 */
const TOP_REVIEWS_STALE_TIME = 5 * 60 * 1000;
const TOP_REVIEWS_GC_TIME = 10 * 60 * 1000;

export const topNewestReviewsQueryOptions = (setLimit = 10) =>
  withSsrCache(
    queryOptions({
      queryKey: ['topNewestReviews', setLimit],
      queryFn: () => API.getTopNewestReviews({ setLimit }),
      staleTime: TOP_REVIEWS_STALE_TIME,
      gcTime: TOP_REVIEWS_GC_TIME
    })
  );

export const useTopNewestReviewsQuery = (setLimit?: number) =>
  useQuery(topNewestReviewsQueryOptions(setLimit));

/**
 * Блок «Отзывы о нас» на главной. Фильтрация пустых текстов и slice(0, 6)
 * перенесены из компонента в select — в кэше лежит сырой ответ.
 */
export const indexReviewsQueryOptions = (reviewsLimit = 20) =>
  withSsrCache(
    queryOptions({
      queryKey: ['indexReviews', reviewsLimit],
      queryFn: () => API.getIndexReviews({ reviewsLimit }),
      select: res =>
        (res.getIndexReviews?.reviews ?? [])
          .filter(item => Number(item?.text?.length) > 0 && item.text !== ' ')
          .slice(0, 6),
      staleTime: TOP_REVIEWS_STALE_TIME,
      gcTime: TOP_REVIEWS_GC_TIME
    })
  );

export const useIndexReviewsQuery = (reviewsLimit?: number) =>
  useQuery(indexReviewsQueryOptions(reviewsLimit));

/**
 * ЭТАП 2 — правый блок «Наш рейтинг» на /reviews (средняя оценка + разбивка
 * по звёздам). Был императивным Promise.all в useEffect с тремя useState —
 * компонент возвращал null до ответа, и SSR отдавал пустоту.
 */
const REVIEWS_RATING_STALE_TIME = 5 * 60 * 1000;
const REVIEWS_RATING_GC_TIME = 10 * 60 * 1000;

export const averageReviewsRatingQueryOptions = () =>
  withSsrCache(
    queryOptions({
      queryKey: ['averageReviewsRating'],
      queryFn: () => API.getAverageReviewsRating({}),
      select: res => res.getAverageReviewsRating ?? 0,
      staleTime: REVIEWS_RATING_STALE_TIME,
      gcTime: REVIEWS_RATING_GC_TIME
    })
  );

export const reviewsRatingQueryOptions = () =>
  withSsrCache(
    queryOptions({
      queryKey: ['reviewsRating'],
      queryFn: () => API.getReviewsRating({}),
      select: res => res.getReviewsRating ?? [],
      staleTime: REVIEWS_RATING_STALE_TIME,
      gcTime: REVIEWS_RATING_GC_TIME
    })
  );
